<template>
  <div>
    <ConfirmationBar
      :showSavebar="showSavebar"
      @confirm="submitForm"
      @cancel="cancel"
    />
    <ConfirmationBar
      :showSavebar="deleteNotification"
      :deleteAction="true"
      @confirm="deleteNotificationAction"
      @cancel="cancelDelete"
    />
    <div class="full-page-width relative locations p-b-40">
      <div class="row">
        <div class="col-xs-8">
          <div class="flex-center m-b-40">
            <div class="flex-center">
              <Btn
                @click.native="routeIt"
                class="light bordered empty d-inline-block m-r-15 fix-40"
                :icon="{
                  name: 'arrowLeftBig',
                  size: '20',
                }"/>
              <p class="user-name">{{ `${headText}` }}</p>
            </div>
          </div>
          <div class="row m-b-40">
            <div class="col-xs-6">
              <div class="button-bar shadowed"
                   :class="isEdit ? 'active brand' : ''"
                   @click="Editor">
                <Icon
                  class="m-r-20"
                  name="code"
                  :circle="true"
                  size="24"/>
                <div class="text-box">
                  <span class="main-text">{{ $t('editor') }}</span>
                </div>
              </div>
            </div>
            <div class="col-xs-6">
              <div
                class="button-bar shadowed"
                :class="!isEdit ? 'active brand' : ''"
                @click="Preview">
                <Icon
                  class="m-r-20"
                  name="preview"
                  :circle="true"
                  size="24"/>
                <div class="text-box">
                  <span class="main-text">{{ $t('preview') }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="row" v-show="isEdit">
            <div class="col-xs-12">
              <div class="card">
                <div class="input-wrapper">
                  <InputText
                    class="m-b-10"
                    :label="notificationBy === 1 ? $t('subject') :  $t('LBL_SMS_SENDER_NAME')"
                    v-model="form.subject"/>
                  <!-- SMS Sender Name Character Counter -->
                  <div v-if="isSmsType" class="char-counter m-b-20"
                       :class="{ 'error': validationErrors.senderName }">
                    <span>{{ senderNameLength }}/{{ smsMaxSenderLength }}</span>
                    <span v-if="validationErrors.senderName" class="validation-error">
                      {{ $t('LBL_SENDER_NAME_TOO_LONG') }}
                    </span>
                  </div>
                </div>

                <div class="textarea-wrapper">
                  <textarea ref="notificationContent" class="editor-box" v-model="form.message"/>
                  <!-- SMS Message Counter -->
                  <div v-if="isSmsType" class="char-counter m-t-10">
                    <span>{{ messageLength }} {{ $t('characters') }} | {{ numberOfSmsMessages }} {{ $t('SMS') }}</span>
                  </div>
                </div>

                <!-- Per-reminder templates for Service Point Reminder -->
                <div v-if="hasReminderLogic" class="m-t-30">
                  <!-- Reminder 2 templates: shown only when Reminder 2 is enabled -->
                  <div v-if="showReminder2Templates">
                    <h3 class="m-b-10">{{ $t('LBL_REMINDER_2_TEMPLATE') || 'Reminder 2 template' }}</h3>
                    <div class="input-wrapper">
                      <InputText
                        class="m-b-10"
                        :label="notificationBy === 1 ? ($t('subject') + ' (' + $t('LBL_SERVICEPOINT_REMINDER_2') + ')') : ($t('LBL_SMS_SENDER_NAME') + ' (R2)')"
                        v-model="form.subject_reminder2"/>
                    </div>
                    <div class="textarea-wrapper m-b-20">
                      <textarea class="editor-box" v-model="form.message_reminder2"/>
                      <div v-if="isSmsType" class="char-counter m-t-10">
                        <span>{{ messageReminder2Length }} {{ $t('characters') }} | {{ numberOfSmsMessagesReminder2 }} {{ $t('SMS') }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Reminder 3 templates: shown only when Reminder 3 is enabled (and Reminder 2 is enabled) -->
                  <div v-if="showReminder3Templates">
                    <h3 class="m-b-10">{{ $t('LBL_REMINDER_3_TEMPLATE') || 'Reminder 3 template' }}</h3>
                    <div class="input-wrapper">
                      <InputText
                        class="m-b-10"
                        :label="notificationBy === 1 ? ($t('subject') + ' (' + $t('LBL_SERVICEPOINT_REMINDER_3') + ')') : ($t('LBL_SMS_SENDER_NAME') + ' (R3)')"
                        v-model="form.subject_reminder3"/>
                    </div>
                    <div class="textarea-wrapper">
                      <textarea class="editor-box" v-model="form.message_reminder3"/>
                      <div v-if="isSmsType" class="char-counter m-t-10">
                        <span>{{ messageReminder3Length }} {{ $t('characters') }} | {{ numberOfSmsMessagesReminder3 }} {{ $t('SMS') }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row" v-show="!isEdit">
            <div class="col-xs-12">
              <div class="card" ref="renderedNotificationContent"></div>
            </div>
          </div>
        </div>
        <div class="col-xs-4">
          <h2 class="page-title heighted">{{ $t('customization') }}</h2>
          <div class="card m-b-40">
            <InputSelect v-if="get_user.is_admin"
                         class="m-b-30"
                         :multiSelect=true
                         :preselected="form.accessible_for_subaccounts"
                         :label="$t('accessible_for_accounts')"
                         @change="selectAccessibleSubaccount"
                         :options="getAllowedSubaccounts"/>
            <ShippingAgentSelect
              :multiSelect="true"
              :preselected="form.shipping_agents"
              :label="$t('shipping_agents')"
              @change="selectShippingAgent($event)"
              class="m-b-30"/>

            <div class="toggle-card m-b-30">
              <div>
                <span class="title">{{ $t('enabled') }}</span>
                <span class="additional-info">{{ $t('show_is_message_enabled') }}</span>
              </div>
              <Toggle id="disableMessage" v-model="form.is_enabled"/>
            </div>
            <div class="toggle-card">
              <div>
                <span class="title">{{ $t('set_as_default') }}</span>
                <span class="additional-info">{{ $t('set_this_message_as_default') }}</span>
              </div>
              <Toggle id="setAsDefault" v-model="form.is_default"/>
            </div>
            <div class="separator medium"></div>
            <CountrySelect
              :multiSelect="true"
              :preselected="countries"
              @change="selectCountries"
              class="m-b-30"
              :label="$t('select_country')"/>
            <InputSelect
              v-if="this.notificationBy === 1"
              :label="$t('SMTP')"
              v-model="form.smtp_id_and_type"
              class="m-b-30"
              :options="get_all_smtps_options"/>
            <InputText
              v-if="this.notificationBy === 1"
              class="m-b-30"
              :label="$t('from_email')"
              v-model="form.from_email"/>
            <InputText
              v-if="this.notificationBy === 1"
              class="m-b-30"
              :label="$t('from_name')"
              v-model="form.from_name"/>
            <InputText
              v-if="this.notificationBy === 1"
              :label="$t('send_bcc_email')"
              v-model="form.bcc_email"/>
            <div v-if="hasReminderLogic">
              <div class="separator medium"></div>
              <div class="toggle-card m-b-20" style="display: block">
                <span class="title">{{ $t('LBL_SERVICEPOINT_REMINDERS') }}</span>
                <span class="additional-info">{{
                    $t('TXT_SERVICEPOINT_REMINDER_DESCRIPTION')
                  }}</span>
              </div>
              <!-- Updated Reminder1 field with visual indicators -->
              <div class="required-field-container m-b-30">
                <InputSelect
                  :label="$t('LBL_SERVICEPOINT_REMINDER_1')"
                  v-model="form.reminder1"
                  :options="getReminderDays"
                  :error="validationErrors.reminder1 ? $t('This field is required and must be greater than 0') : ''"
                />
              </div>

              <!-- Updated Reminder2 field with visual indicators -->
              <div class="field-container m-b-30">
                <InputSelect
                  :label="$t('LBL_SERVICEPOINT_REMINDER_2')"
                  v-model="form.reminder2"
                  :options="getReminderDays"
                  :error="validationErrors.reminder2 ? $t('Reminder 2 cannot be set without Reminder 1 being greater than 0') : ''"
                  :disabled="!isReminder1Valid"
                />
              </div>

              <!-- New Reminder3 field with visual indicators -->
              <div class="field-container m-b-30">
                <InputSelect
                  :label="$t('LBL_SERVICEPOINT_REMINDER_3')"
                  v-model="form.reminder3"
                  :options="getReminderDays"
                  :error="validationErrors.reminder3 ? $t('Reminder 3 cannot be set without Reminder 1 being greater than 0') : ''"
                  :disabled="!isReminder2Enabled"
                />
              </div>
            </div>
            <div class="separator medium"></div>
            <div class="toggle-card">
              <div>
                <span class="title">{{ $t('delete') }}</span>
                <span class="additional-info">{{ $t('delete_this_notification') }}</span>
              </div>
              <Toggle color="red" id="deleteNotification" v-model="deleteNotification"/>
            </div>
          </div>
          <InfoTextBox :title="$t('notification_var_title')" :text="$t('notification_var_text')"/>
          <div class="card variable-box m-b-40">
            <span v-for="(item, index) in variables[notificationType]" :key="`variable-${index}`">
              {{ item }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import {mapGetters} from 'vuex';
import UserCard from '@/components/global/userCard';
import InputText from '@/components/elements/inputs/text.vue';
import Btn from '@/components/elements/buttons';
import Toggle from '@/components/elements/inputs/toggle.vue';
import CountrySelect from '@/components/elements/inputs/countrySelect.vue';
import ConfirmationBar from '@/components/global/confirmationBar';
import InputSelect from '@/components/elements/inputs/select.vue';

import InfoTextBox from '@/components/global/infoTextBox';
import mixin from '@/mixins/mixins.js';
import ShippingAgentSelect from "@/components/elements/inputs/shippingAgentSelect.vue";

export default {
  name: 'customizeNotification',
  mixins: [mixin],
  components: {
    ShippingAgentSelect,
    UserCard,
    InputText,
    Btn,
    Toggle,
    CountrySelect,
    InfoTextBox,
    ConfirmationBar,
    InputSelect
  },
  data() {
    return {
      deleteNotification: false,
      showSavebar: false,
      savebarEnabled: false,
      isVisibleBack: this.$route.params.id,
      isEdit: true,
      userId: '',
      senderName: '',
      senderCompanyName: '',
      notificationType: '',
      notificationBy: '',
      form: {
        accessible_for_subaccounts: [],
        message: '',
        is_enabled: '',
        is_default: '',
        from_email: '',
        from_name: '',
        bcc_email: '',
        subject: '',
        subject_reminder2: '',
        message_reminder2: '',
        subject_reminder3: '',
        message_reminder3: '',
        countries_json: [],
        notification_type: '',
        notification_by: this.notificationBy,
        smtp_id_and_type: '',
        reminder1: '',
        reminder2: '',
        reminder3: '',
        shipping_agents: [],
      },
      countries: [],
      variables: [],
      renderedNotificationContent: '',
      // Updated validation properties to include reminders
      validationErrors: {
        senderName: false,
        reminder1: false,
        reminder2: false,
        reminder3: false
      },
      smsMaxSenderLength: 11,
      smsCharLimit: 160
    };
  },
  computed: {
    ...mapGetters([
      'get_notifications',
      'get_active_notification',
      'get_notification_by_id',
      'get_all_smtps_options',
      'get_user',
      'get_subaccounts'
    ]),
    hasReminderLogic() {
      return [7].includes(parseInt(this.notificationType))
    },
    hasCarrierLogic() {
      return [7].includes(parseInt(this.notificationType))
    },
    getAllowedSubaccounts() {
      return this.get_subaccounts.filter((acc) => this.get_user.accessible_subaccounts_array.indexOf(String(acc.id)) > -1).map((acc) => ({
        text: acc.name,
        value: String(acc.id),
      }));
    },
    notificationByText() {
      const notBy = this.notificationBy
      if (parseInt(notBy) === 1) return 'E-mail';
      if (parseInt(notBy) === 2) return 'SMS';
      return '';
    },
    getReminderDays() {
      // Include a "NO" option (0) for optional reminders (R2/R3). R1 validation still enforces > 0
      const options = [{ value: 0, text: this.$t('LBL_NO') }];
      for (let i = 1; i <= 14; i++) {
        options.push({ value: i, text: i.toString()});
      }
      return options;
    },
    notificationTypeText() {
      const notBy = this.notificationType
      if (parseInt(notBy) === 1) return this.$t('Shipment notification').toLowerCase();
      if (parseInt(notBy) === 2) return this.$t('waybill notification').toLowerCase();
      if (parseInt(notBy) === 3) return this.$t('delivery notification').toLowerCase();
      if (parseInt(notBy) === 4) return this.$t('deviation notification').toLowerCase();
      if (parseInt(notBy) === 5) return this.$t('status change notification').toLowerCase();
      if (parseInt(notBy) === 6) return this.$t('return waybill notification').toLowerCase();
      if (parseInt(notBy) === 7) return this.$t('SERVICEPOINT_REMINDER_NOTIFICATION').toLowerCase();
      return '';
    },
    headText() {
      let senderName = this.senderCompanyName ? this.senderCompanyName : this.senderName;
      senderName = senderName ? `${this.$t('for')} ${senderName}` : '';

      return `${this.notificationByText + ' ' + this.notificationTypeText + ' ' + senderName}`;
    },
    // Add SMS validation checks
    isSmsType() {
      return parseInt(this.notificationBy) === 2;
    },
    senderNameLength() {
      return this.form.subject ? this.form.subject.length : 0;
    },
    messageLength() {
      return this.form.message ? this.form.message.length : 0;
    },
    numberOfSmsMessages() {
      if (!this.isSmsType) return 0;
      return this.countSmsSegments(this.form.message);
    },
    messageReminder2Length() {
      return this.form.message_reminder2 ? this.form.message_reminder2.length : 0;
    },
    numberOfSmsMessagesReminder2() {
      if (!this.isSmsType) return 0;
      return this.countSmsSegments(this.form.message_reminder2);
    },
    messageReminder3Length() {
      return this.form.message_reminder3 ? this.form.message_reminder3.length : 0;
    },
    numberOfSmsMessagesReminder3() {
      if (!this.isSmsType) return 0;
      return this.countSmsSegments(this.form.message_reminder3);
    },
    isSenderNameValid() {
      if (this.isSmsType) {
        return this.senderNameLength <= this.smsMaxSenderLength;
      }
      return true;
    },
    // Add reminder validation computed properties
    isReminder1Valid() {
      // Only validate if the reminder logic is applicable
      if (!this.hasReminderLogic) return true;

      // reminder1 must be set (not empty or zero)
      return this.form.reminder1 !== '' &&
        this.form.reminder1 !== null &&
        parseInt(this.form.reminder1) > 0;
    },
    isReminder2Valid() {
      // Only validate if reminder logic is applicable and reminder2 is set
      if (!this.hasReminderLogic) return true;
      if (this.form.reminder2 === '' || parseInt(this.form.reminder2) === 0) return true;

      // If reminder2 is set, reminder1 must also be set
      return this.isReminder1Valid;
    },
    isReminder3Valid() {
      // Only validate if reminder logic is applicable and reminder3 is set
      if (!this.hasReminderLogic) return true;
      if (this.form.reminder3 === '' || parseInt(this.form.reminder3) === 0) return true;
      return this.isReminder1Valid;
    },
    // Enablement flags based on dependency between reminders
    isReminder2Enabled() {
      return this.hasReminderLogic && this.isReminder1Valid && parseInt(this.form.reminder2) > 0;
    },
    isReminder3Enabled() {
      return this.hasReminderLogic && this.isReminder2Enabled && parseInt(this.form.reminder3) > 0;
    },
    // Show template inputs only when corresponding reminder is enabled
    showReminder2Templates() {
      return this.isReminder2Enabled;
    },
    showReminder3Templates() {
      return this.isReminder3Enabled;
    },
    // Helper computed property to check if reminder2 should be disabled
    shouldDisableReminder2() {
      return !this.isReminder1Valid;
    },
    // Update formIsValid to include reminder validation with proper conditional logic
    formIsValid() {
      // Basic validation for all forms
      let isValid = this.isSenderNameValid;

      // If reminder logic applies, ensure reminder fields are valid
      if (this.hasReminderLogic) {
        isValid = isValid && this.isReminder1Valid && this.isReminder2Valid && this.isReminder3Valid;
      }

      return isValid;
    }
  },
  created() {
    this.$store.dispatch('GET_NOTIFICATION', this.$route.params.id).then((res) => {
      this.form.accessible_for_subaccounts = res.items.accessible_for_subaccounts
      this.form.message = res.items.message;
      this.form.is_default = res.items.is_default;
      this.form.is_enabled = res.items.is_enabled;
      this.form.subject = res.items.subject;
      this.form.subject_reminder2 = res.items.subject_reminder2;
      this.form.message_reminder2 = res.items.message_reminder2;
      this.form.subject_reminder3 = res.items.subject_reminder3;
      this.form.message_reminder3 = res.items.message_reminder3;
      this.form.from_email = res.items.from_email;
      this.form.from_name = res.items.from_name;
      this.form.notification_by = res.items.notification_by;
      this.form.bcc_email = res.items.bcc_email;
      this.form.reminder1 = res.items.reminder1;
      this.form.reminder2 = res.items.reminder2;
      this.form.reminder3 = res.items.reminder3;
      let shippingAgents = res.items.shipping_agents || [];
      if (typeof shippingAgents === 'string') {
        try {
          shippingAgents = JSON.parse(shippingAgents);
        } catch (e) {
          shippingAgents = [];
        }
      }
      this.form.shipping_agents = Array.isArray(shippingAgents) ? shippingAgents : [];
      this.form.countries_json = res.items.countries_array.map((country) => {
        return {
          country_id: country,
          country_name: this.getCountry(country, 'name'),
        };
      }),
        this.countries = res.items.countries_array || [];
      this.senderName = res.items.sender_name;
      this.senderCompanyName = res.items.sender_company_name;
      this.notificationType = res.items.notification_type
      this.notificationBy = res.items.notification_by;
      this.variables = res.entity.variables;
      this.form.notification_type = res.items.notification_type;
      this.form.smtp_id_and_type = res.items.smtp_id_and_type;
      this.$nextTick(() => {
        this.savebarEnabled = true;
        // Perform initial validation
        this.validateForm();

        // If this is a reminder notification and reminder1 is not set properly, hide save bar
        if (this.hasReminderLogic &&
          (!this.form.reminder1 ||
            this.form.reminder1 === '' ||
            parseInt(this.form.reminder1) === 0 ||
            parseInt(this.form.reminder1) < 1)) {
          this.showSavebar = false;
          this.validationErrors.reminder1 = true;
        }
      })
    });

    this.$store.dispatch('GET_ALL_SMTPS');
  },
  methods: {
    // SMS counting utils (GSM-7/UCS-2 aligned with backend)
    isGsm7(text) {
      if (!text) return true;
      const basic = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
      const extended = "^{}\\[~]|€";
      const allowed = basic + extended;
      for (const ch of text) {
        if (!allowed.includes(ch)) return false;
      }
      return true;
    },
    countSmsSegments(text) {
      const t = text || '';
      if (t.length === 0) return 0;
      const extended = "^{}\\[~]|€";
      if (this.isGsm7(t)) {
        let septets = 0;
        for (const ch of t) {
          septets += extended.includes(ch) ? 2 : 1;
        }
        const singleLimit = 160;
        const concatLimit = 153;
        return septets <= singleLimit ? 1 : Math.ceil(septets / concatLimit);
      }
      // UCS-2: JavaScript string length is UTF-16 code units
      const units = t.length;
      const singleLimit = 70;
      const concatLimit = 67;
      return units <= singleLimit ? 1 : Math.ceil(units / concatLimit);
    },
    selectAccessibleSubaccount(val) {
      this.form.accessible_for_subaccounts = val;
    },
    selectShippingAgent(val) {
      this.form.shipping_agents = val;
    },
    routeIt() {
      this.$router.push('/settings/shipping/notifications');
    },
    validateForm() {
      // Reset all validation errors
      this.validationErrors = {
        senderName: false,
        reminder1: false,
        reminder2: false
      };

      // SMS validation
      if (this.isSmsType) {
        this.validationErrors.senderName = !this.isSenderNameValid;
      }

      // Reminder validation (only check if reminder logic applies)
      if (this.hasReminderLogic) {
        this.validationErrors.reminder1 = !this.isReminder1Valid;
        this.validationErrors.reminder2 = !this.isReminder2Valid;
        this.validationErrors.reminder3 = !this.isReminder3Valid;
      }

      return this.formIsValid;
    },
    submitForm() {
      // Double-check validation before submission
      const isValid = this.validateForm();

      if (!isValid) {
        // Don't submit and ensure validation errors are shown
        this.showSavebar = false;
        return;
      }

      this.$store.dispatch('EDIT_NOTIFICATION', {
        id: this.$route.params.id,
        form: this.form,
      }).then(() => {
        this.$store.dispatch('GET_NOTIFICATIONS');
        this.showSavebar = false;
      });
    },
    cancel() {
      this.showSavebar = false;
    },
    Preview() {
      this.isEdit = false;
      if (this.$refs.renderedNotificationContent) {
        let html = '';

        // Main template preview
        if (this.$refs.notificationContent !== undefined) {
          const labelMain = parseInt(this.notificationBy) === 1 ? this.$t('subject') : this.$t('LBL_SMS_SENDER_NAME');
          html += `<h3>${this.$t('preview')}</h3>`;
          html += `<div><strong>${labelMain}:</strong> ${this.form.subject || ''}</div>`;
          html += `<div class="m-t-10">${this.$refs.notificationContent.value || ''}</div>`;
        }

        // Reminder 2 preview (only if supported by message type and content exists)
        if (this.hasReminderLogic && (this.form.subject_reminder2 || this.form.message_reminder2)) {
          const labelR2 = parseInt(this.notificationBy) === 1 ? this.$t('subject') : this.$t('LBL_SMS_SENDER_NAME');
          html += `<hr/>`;
          html += `<h3>${this.$t('LBL_REMINDER_2_TEMPLATE') || 'Reminder 2 template'}</h3>`;
          html += `<div><strong>${labelR2}:</strong> ${this.form.subject_reminder2 || ''}</div>`;
          html += `<div class="m-t-10">${this.form.message_reminder2 || ''}</div>`;
        }

        // Reminder 3 preview (only if supported by message type and content exists)
        if (this.hasReminderLogic && (this.form.subject_reminder3 || this.form.message_reminder3)) {
          const labelR3 = parseInt(this.notificationBy) === 1 ? this.$t('subject') : this.$t('LBL_SMS_SENDER_NAME');
          html += `<hr/>`;
          html += `<h3>${this.$t('LBL_REMINDER_3_TEMPLATE') || 'Reminder 3 template'}</h3>`;
          html += `<div><strong>${labelR3}:</strong> ${this.form.subject_reminder3 || ''}</div>`;
          html += `<div class="m-t-10">${this.form.message_reminder3 || ''}</div>`;
        }

        this.$refs.renderedNotificationContent.innerHTML = html || '';
      }
    },
    Editor() {
      this.isEdit = true;
    },
    selectCountries(val) {
      this.countries = val;
      this.form.countries_json = val.map((country) => {
        return {
          country_id: country,
          country_name: this.getCountry(country, 'name'),
        };
      });
    },
    deleteNotificationAction() {
      this.$store.dispatch('DELETE_NOTIFICATION', this.$route.params.id).then(() => {
        this.$router.push('/settings/shipping/notifications');
      });
    },
    cancelDelete() {
      this.deleteNotification = false;
    }
  },
  watch: {
    'form': {
      handler() {
        const isValid = this.validateForm();
        if (this.savebarEnabled) {
          this.showSavebar = isValid;

          // If the user already tried to submit an invalid form, validate immediately
          if (this.hasReminderLogic && this.form.reminder1 === '') {
            this.validationErrors.reminder1 = true;
          }
        }
      },
      deep: true
    },

    // Specifically watch reminder1 for validation
    'form.reminder1': function (newVal) {
      if (this.hasReminderLogic) {
        // Check if reminder1 is empty, null or 0
        this.validationErrors.reminder1 = (newVal === '' ||
          newVal === null ||
          parseInt(newVal) === 0 ||
          parseInt(newVal) < 1);

        // If reminder1 is invalid, ensure reminder2 is also validated
        if (this.validationErrors.reminder1) {
          // If reminder2 has a value, it's also invalid
          if (this.form.reminder2 && parseInt(this.form.reminder2) > 0) {
            this.validationErrors.reminder2 = true;
          }

          // Ensure the save bar is hidden
          if (this.savebarEnabled) {
            this.showSavebar = false;
          }
        }
      }
    },

    // Add watcher for reminder2
    'form.reminder2': function (newVal) {
      if (!this.hasReminderLogic) return;
      const val = newVal === '' || newVal === null ? 0 : parseInt(newVal);
      if (val > 0) {
        // If reminder2 has a value, ensure reminder1 is valid
        this.validationErrors.reminder2 = !this.isReminder1Valid;
        if (this.validationErrors.reminder2 && this.savebarEnabled) {
          this.showSavebar = false;
        }
      } else {
        // NO selected for reminder2 -> reset its templates and disable/reset reminder3
        this.validationErrors.reminder2 = false;
        this.form.subject_reminder2 = '';
        this.form.message_reminder2 = '';
        // Force reminder3 to NO and clear its templates as R3 depends on R2
        this.form.reminder3 = 0;
        this.form.subject_reminder3 = '';
        this.form.message_reminder3 = '';
      }
    },

    // Add watcher for reminder3
    'form.reminder3': function (newVal) {
      if (!this.hasReminderLogic) return;
      const val = newVal === '' || newVal === null ? 0 : parseInt(newVal);
      if (val > 0) {
        // If reminder3 has a value, ensure reminder1 (and R2) are valid
        this.validationErrors.reminder3 = !this.isReminder1Valid || !(parseInt(this.form.reminder2) > 0);
        if (this.validationErrors.reminder3 && this.savebarEnabled) {
          this.showSavebar = false;
        }
      } else {
        // NO selected for reminder3 -> reset its templates
        this.validationErrors.reminder3 = false;
        this.form.subject_reminder3 = '';
        this.form.message_reminder3 = '';
      }
    }
  },
};
</script>
<style>
.char-counter {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
}

.char-counter.error {
  color: #e74c3c;
}

.validation-error {
  color: #e74c3c;
  font-weight: 500;
}

.textarea-wrapper, .input-wrapper {
  position: relative;
}

.editor-box {
  width: 100%;
  min-height: 300px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: inherit;
  resize: vertical;
}

.editor-box.error {
  border-color: #e74c3c;
}

.input-error {
  color: #e74c3c;
  font-size: 12px;
  margin-top: 4px;
}

/* Add styling for required field indicators */
.required-field-container, .field-container {
  position: relative;
}

.required-indicator {
  color: #e74c3c;
  position: absolute;
  top: 0;
  right: -10px;
  font-size: 16px;
  font-weight: bold;
}

.field-description {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  font-style: italic;
}

.field-description.error {
  color: #e74c3c;
  font-weight: 500;
  font-style: normal;
}

/* Style for a disabled input */
.input-select.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
